import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Save, ArrowUp, ArrowDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LANGS = [
  { code: 'ca', label: 'Català' },
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
];

const EMPTY_ML = { ca: '', es: '', en: '', fr: '' };

const MLField = ({ label, value, onChange, multiline = false, placeholder = '', testidBase }) => {
  const [lang, setLang] = useState('ca');
  const v = value || EMPTY_ML;
  const InputComp = multiline ? Textarea : Input;
  const tid = (testidBase || (label || '').toLowerCase().replace(/\s+/g, '-')) + '-' + lang;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label className="text-sm font-medium">{label}</Label>
        <div className="flex gap-1">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => setLang(l.code)}
              className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded ${
                lang === l.code ? 'bg-emerald-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {l.code}
            </button>
          ))}
        </div>
      </div>
      <InputComp
        value={v[lang] || ''}
        onChange={(e) => onChange({ ...v, [lang]: e.target.value })}
        placeholder={placeholder || `Texto en ${LANGS.find((x) => x.code === lang)?.label}`}
        className={multiline ? 'min-h-[100px]' : ''}
        data-testid={`onia-${tid}`}
      />
    </div>
  );
};

export const OnIaGolfContentForm = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    axios.get(`${API_URL}/api/on-ia-golf-content`)
      .then((r) => setContent(r.data))
      .catch(() => toast.error('Error cargando contenido ON-IA-GOLF'))
      .finally(() => setLoading(false));
  }, []);

  const update = (field, value) => setContent((c) => ({ ...c, [field]: value }));

  const updateCommunity = (idx, field, value) => {
    setContent((c) => {
      const arr = [...(c.communities || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...c, communities: arr };
    });
  };

  const updateApp = (idx, field, value) => {
    setContent((c) => {
      const arr = [...(c.apps || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...c, apps: arr };
    });
  };

  const moveItem = (key, idx, dir) => {
    setContent((c) => {
      const arr = [...(c[key] || [])];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= arr.length) return c;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      arr.forEach((it, i) => (it.order = i + 1));
      return { ...c, [key]: arr };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/on-ia-golf-content`, content, { headers });
      toast.success('ON-IA-GOLF guardado');
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-slate-500">Cargando…</div>;
  if (!content) return <div className="p-6 text-red-600">No se pudo cargar el contenido.</div>;

  return (
    <div className="space-y-6" data-testid="onia-admin-form">
      <div className="flex items-center justify-between sticky top-0 bg-white z-10 py-3 -mt-3 border-b">
        <div>
          <h2 className="text-2xl font-bold">ON-IA-GOLF</h2>
          <p className="text-sm text-slate-500">Página pública en /on-ia-golf — editable en 4 idiomas</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="onia-save-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </div>

      {/* HERO */}
      <Card>
        <CardHeader>
          <CardTitle>Hero</CardTitle>
          <CardDescription>Encabezado superior de la página</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <MLField label="Eyebrow" testidBase="eyebrow" value={content.eyebrow} onChange={(v) => update('eyebrow', v)} />
          <MLField label="Título Hero" testidBase="hero-title" value={content.hero_title} onChange={(v) => update('hero_title', v)} />
          <MLField label="Subtítulo Hero" testidBase="hero-subtitle" value={content.hero_subtitle} onChange={(v) => update('hero_subtitle', v)} multiline />
        </CardContent>
      </Card>

      {/* WHAT IS */}
      <Card>
        <CardHeader>
          <CardTitle>¿Qué es Hoyo Zero?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <MLField label="Título" testidBase="what-title" value={content.what_title} onChange={(v) => update('what_title', v)} />
          <MLField label="Cuerpo" testidBase="what-body" value={content.what_body} onChange={(v) => update('what_body', v)} multiline />
        </CardContent>
      </Card>

      {/* COMMUNITIES */}
      <Card>
        <CardHeader>
          <CardTitle>Comunidades</CardTitle>
          <CardDescription>{content.communities?.length || 0} comunidades en orden</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <MLField label="Título sección" testidBase="comm-title" value={content.communities_title} onChange={(v) => update('communities_title', v)} />
          <MLField label="Intro sección" testidBase="comm-intro" value={content.communities_intro} onChange={(v) => update('communities_intro', v)} multiline />
          <div className="space-y-4">
            {(content.communities || []).map((c, idx) => (
              <div key={c.key + idx} className="border rounded-lg p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-medium text-slate-800">
                    <span className="mr-2">{c.emoji}</span>{c.name} <span className="text-xs text-slate-500 ml-2">({c.key})</span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => moveItem('communities', idx, -1)}><ArrowUp className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => moveItem('communities', idx, 1)}><ArrowDown className="w-3 h-3" /></Button>
                    <label className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={c.active !== false} onChange={(e) => updateCommunity(idx, 'active', e.target.checked)} />
                      Activo
                    </label>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Emoji</Label>
                    <Input value={c.emoji || ''} onChange={(e) => updateCommunity(idx, 'emoji', e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Nombre (sin traducir)</Label>
                    <Input value={c.name || ''} onChange={(e) => updateCommunity(idx, 'name', e.target.value)} />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">URL interna</Label>
                    <Input value={c.url || ''} onChange={(e) => updateCommunity(idx, 'url', e.target.value)} />
                  </div>
                </div>
                <div className="mt-3 space-y-3">
                  <MLField label="Descripción" testidBase={`comm-desc-${c.key}`} value={c.description} onChange={(v) => updateCommunity(idx, 'description', v)} multiline />
                  <MLField label="Texto CTA" testidBase={`comm-cta-${c.key}`} value={c.cta} onChange={(v) => updateCommunity(idx, 'cta', v)} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* APPS */}
      <Card>
        <CardHeader>
          <CardTitle>Apps</CardTitle>
          <CardDescription>{content.apps?.length || 0} apps</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <MLField label="Título sección" testidBase="apps-title" value={content.apps_title} onChange={(v) => update('apps_title', v)} />
          <MLField label="Intro sección" testidBase="apps-intro" value={content.apps_intro} onChange={(v) => update('apps_intro', v)} multiline />
          <div className="space-y-4">
            {(content.apps || []).map((a, idx) => (
              <div key={a.key + idx} className="border rounded-lg p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-medium text-slate-800">
                    <span className="mr-2">{a.emoji}</span>{a.title} <span className="text-xs text-slate-500 ml-2">({a.key})</span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => moveItem('apps', idx, -1)}><ArrowUp className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => moveItem('apps', idx, 1)}><ArrowDown className="w-3 h-3" /></Button>
                    <label className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={a.active !== false} onChange={(e) => updateApp(idx, 'active', e.target.checked)} />
                      Activo
                    </label>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Emoji</Label>
                    <Input value={a.emoji || ''} onChange={(e) => updateApp(idx, 'emoji', e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Título (sin traducir)</Label>
                    <Input value={a.title || ''} onChange={(e) => updateApp(idx, 'title', e.target.value)} />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">URL externa</Label>
                    <Input value={a.url || ''} onChange={(e) => updateApp(idx, 'url', e.target.value)} />
                  </div>
                </div>
                <div className="mt-3">
                  <MLField label="Descripción" testidBase={`apps-desc-${a.key}`} value={a.description} onChange={(v) => updateApp(idx, 'description', v)} multiline />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="sticky bottom-0 bg-white py-3 border-t flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Guardando…' : 'Guardar todo'}
        </Button>
      </div>
    </div>
  );
};

export default OnIaGolfContentForm;
